import { useCallback, useEffect } from 'react';
import useUsers from '../utils/useUsers';
import { UserState } from '../../../../utils/apollo/graphqlSchema';
import { useAppSelector } from '../../../../redux/hooks';
import { selectOpenedModalType } from '../../../../redux/selectors/modal';
import { ModalSliceType } from '../../../../redux/slices/modal';
import UsersFilters from '../components/UsersFilters';
import PermissionToggleModal from '../../../PermissionControls/PermissionToggleModal';
import DisableUserModal from '../components/DisableUserModal';
import UsersTable from '../components/UsersTable';
import { ContentLoader } from '../../../../components/Loaders';
import UserResendInviteModal from '../components/UserResendInviteModal';
import UserRevokeModal from '../components/UserRevokeModal';
import InviteUsersModal from '../components/InviteUsersModal';
import { useAnalytics } from '../../../../utils/analytics/useAnalytics';
import { pageViewed } from '../../../../utils/analytics/events/page/events';
import useCustomerPlan from '../../../../utils/hooks/useCustomerPlan';
import InPageBanner from '../../../../components/InPageBanner';
import CustomSeatsExceedWarning from './CustomSeatsExceedWarning';

const userStates = [UserState.Active, UserState.Pending, UserState.Deleted];

const AllUsers = () => {
  const {
    refetch: refetchUsers,
    loading,
    users,
    total,
    searchTerm,
    setSearchTerm,
    roles,
    setRoles,
    states,
    setStates,
  } = useUsers(userStates);

  const { maxSeats, refetchCustomerPlan } = useCustomerPlan();

  const { track } = useAnalytics();

  useEffect(() => {
    track(
      pageViewed({
        category: 'settings',
        name: 'users',
        id: 'all',
      }),
    );
  }, [track]);

  const refetch = useCallback(async () => {
    await Promise.allSettled([refetchUsers(), refetchCustomerPlan()]);
  }, [refetchCustomerPlan, refetchUsers]);

  const isUserResendInviteModalOpen = useAppSelector((state) =>
    selectOpenedModalType(state, ModalSliceType.UserInviteResend, ''),
  );
  const isRevokeUserModalOpen = useAppSelector((state) =>
    selectOpenedModalType(state, ModalSliceType.RevokeUser, ''),
  );
  const isDisableUserModalOpen = useAppSelector((state) =>
    selectOpenedModalType(state, ModalSliceType.DisableUser, ''),
  );
  const isEnableUserModalOpen = useAppSelector((state) =>
    selectOpenedModalType(state, ModalSliceType.EnableUser, ''),
  );
  const isInviteNewUsersModalOpen = useAppSelector((state) =>
    selectOpenedModalType(state, ModalSliceType.UserInviteNewUsers, ''),
  );

  return (
    <>
      {maxSeats && maxSeats.availableSeats <= 0 && (
        <InPageBanner type="warning">
          <CustomSeatsExceedWarning maxSeats={maxSeats} />
        </InPageBanner>
      )}
      <UsersFilters
        total={total}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        roles={roles}
        setRoles={setRoles}
        states={states}
        setStates={setStates}
        pendingUsersRoute={false}
        maxSeats={maxSeats}
      />
      {loading ? (
        <ContentLoader />
      ) : (
        <UsersTable users={users} pendingUsersRoute={false} />
      )}
      {isUserResendInviteModalOpen && (
        <UserResendInviteModal onSubmit={refetch} />
      )}
      {isRevokeUserModalOpen && <UserRevokeModal onSubmit={refetch} />}
      {isEnableUserModalOpen && <PermissionToggleModal refetch={refetch} />}
      {isDisableUserModalOpen && <DisableUserModal refetch={refetch} />}
      {isInviteNewUsersModalOpen && <InviteUsersModal onSubmit={refetch} />}
    </>
  );
};

export default AllUsers;
